import { useEffect, useState } from 'react'
import type { FormEvent } from 'react'
import { Trash2 } from 'lucide-react'
import { cn } from '../../lib/utils'
import { SecretAuditPanel } from './secret-audit-panel'
import { SecretGrantsPanel } from './secret-grants-panel'
import { SecretRevealPanel } from './secret-reveal-panel'
import { SecretVersionsPanel } from './secret-versions-panel'
import type {
  SecretAuditEvent,
  SecretEntry,
  SecretGrant,
  SecretGrantDraft,
  SecretKind,
  SecretUpdateDraft,
  SecretVersion,
} from './secret-dashboard-types'

interface SecretDetailPanelProps {
  entry: SecretEntry | null
  versions: SecretVersion[]
  grants: SecretGrant[]
  auditEvents: SecretAuditEvent[]
  isDark: boolean
  isUpdating: boolean
  isCreatingGrant: boolean
  onUpdate: (draft: SecretUpdateDraft) => Promise<void>
  onDisable: (entry: SecretEntry) => void
  onDestroy: (entry: SecretEntry) => void
  onRollback: (version: SecretVersion) => void
  onCreateGrant: (draft: SecretGrantDraft & { expiresAt: number | null }) => Promise<void>
  onRevokeGrant: (grant: SecretGrant) => void
}

function inputClass(isDark: boolean) {
  return cn(
    'w-full rounded-md border px-3 py-2 text-base outline-none md:text-sm',
    isDark ? 'border-white/[0.08] bg-surface-2 text-neutral-100 placeholder-neutral-600' : 'border-neutral-200 bg-white text-neutral-900 placeholder-neutral-400',
  )
}

export function SecretDetailPanel({
  entry,
  versions,
  grants,
  auditEvents,
  isDark,
  isUpdating,
  isCreatingGrant,
  onUpdate,
  onDisable,
  onDestroy,
  onRollback,
  onCreateGrant,
  onRevokeGrant,
}: SecretDetailPanelProps) {
  const [draft, setDraft] = useState<SecretUpdateDraft>({ value: '', valueChanged: false, kind: 'secret', description: '', reason: 'web update' })

  useEffect(() => {
    if (!entry) return
    setDraft({
      value: '',
      valueChanged: false,
      kind: entry.kind,
      description: entry.description ?? '',
      reason: 'web update',
    })
  }, [entry?.id, entry?.kind, entry?.description])

  async function handleUpdate(event: FormEvent) {
    event.preventDefault()
    await onUpdate(draft)
    setDraft((current) => ({ ...current, value: '', valueChanged: false }))
  }

  return (
    <aside className={cn('overflow-auto border-t p-4 lg:border-l lg:border-t-0', isDark ? 'border-white/[0.06] bg-surface-1' : 'border-neutral-200 bg-white')}>
      {!entry ? (
        <p className="text-sm text-neutral-500">Select an entry to manage value changes, versions, grants, reveal, and audit.</p>
      ) : (
        <div className="space-y-5">
          <div>
            <div className="font-mono text-sm font-semibold">{entry.key}</div>
            <div className="text-xs text-neutral-500">{entry.environment}{entry.path} · v{entry.currentVersion}</div>
          </div>

          <form onSubmit={handleUpdate} className="space-y-2">
            <div className="text-sm font-medium">Update</div>
            <input className={inputClass(isDark)} value={draft.value} onChange={(e) => setDraft((s) => ({ ...s, value: e.target.value, valueChanged: true }))} placeholder="New value (leave untouched to keep current)" />
            <select className={inputClass(isDark)} value={draft.kind} onChange={(e) => setDraft((s) => ({ ...s, kind: e.target.value as SecretKind }))}>
              <option value="secret">secret</option>
              <option value="value">value</option>
            </select>
            <input className={inputClass(isDark)} value={draft.description} onChange={(e) => setDraft((s) => ({ ...s, description: e.target.value }))} placeholder="Description" />
            <input className={inputClass(isDark)} value={draft.reason} onChange={(e) => setDraft((s) => ({ ...s, reason: e.target.value }))} placeholder="Reason" />
            <button disabled={isUpdating || !draft.reason.trim()} className="rounded-md bg-brand-600 px-3 py-2 text-sm text-white disabled:opacity-50">
              {isUpdating ? 'Updating...' : `Update v${entry.currentVersion + 1}`}
            </button>
          </form>

          <SecretRevealPanel entry={entry} isDark={isDark} />
          <SecretVersionsPanel versions={versions} isDark={isDark} onRollback={onRollback} />
          <SecretGrantsPanel entry={entry} grants={grants} isDark={isDark} isCreating={isCreatingGrant} onCreateGrant={onCreateGrant} onRevokeGrant={onRevokeGrant} />
          <SecretAuditPanel events={auditEvents} isDark={isDark} />

          <div className="flex flex-wrap gap-2 border-t border-red-500/20 pt-4">
            <button type="button" onClick={() => onDisable(entry)} className="inline-flex items-center gap-2 rounded-md px-3 py-2 text-sm text-red-600 hover:bg-red-500/10">
              <Trash2 className="h-4 w-4" /> Disable
            </button>
            <button type="button" onClick={() => onDestroy(entry)} className="inline-flex items-center gap-2 rounded-md bg-red-600 px-3 py-2 text-sm text-white hover:bg-red-500">
              <Trash2 className="h-4 w-4" /> Destroy
            </button>
          </div>
        </div>
      )}
    </aside>
  )
}
